import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Tabs,
  Tab,
  Chip,
  Button,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  useMediaQuery,
  Stack,
  Avatar,
  Divider,
} from '@mui/material';
import {
  Star,
  CheckCircle,
  Clock,
  AlertCircle,
  Calendar,
  User,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';

import { useAppSelector, useAppDispatch } from '@/store';
import { selectCurrentUser } from '@/store/slices/authSlice';
import { openModal } from '@/store/slices/uiSlice';
import { reviewService, performanceService, leaveService } from '@/services/api';
import { QUERY_KEYS } from '@/services/queryClient';
import { Review, ReviewStatus, UserRole, LeaveRequest, LeaveStatus } from '@/types';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`review-tabpanel-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

/**
 * ReviewsPage
 * Task reviews and approval workflow
 * For both individuals and managers/reviewers
 */
const ReviewsPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectCurrentUser);

  const handleReview = (review: Review) => {
    dispatch(openModal({
      type: 'reviewFeedback',
      data: {
        reviewId: review.id,
        employeeName: review.targetId,
        reviewType: review.type === 'team' ? 'manager' : 'peer',
      },
    }));
  };
  const [activeTab, setActiveTab] = useState(0);

  const isReviewer = user && [UserRole.TEAM_LEAD, UserRole.PROJECT_MANAGER, UserRole.DEPARTMENT_HEAD, UserRole.ADMIN].includes(user.role);

  // Fetch all reviews (backend returns { success, data: { items, total, page, ... } })
  const { data: reviewsData, isLoading: reviewsLoading } = useQuery({
    queryKey: QUERY_KEYS.reviews,
    queryFn: () => reviewService.getReviews(),
  });

  const reviewsRaw = reviewsData?.data;
  const reviews = Array.isArray(reviewsRaw) ? reviewsRaw : (reviewsRaw?.items ?? []);

  // Fetch pending task reviews that I need to review
  const { data: pendingData, isLoading: pendingLoading } = useQuery({
    queryKey: QUERY_KEYS.myPendingReviews,
    queryFn: () => reviewService.getReviews({ status: ReviewStatus.PENDING, reviewerId: user?.id }),
    enabled: !!user && !!isReviewer,
  });

  const pendingRaw = pendingData?.data;
  const pendingReviews = Array.isArray(pendingRaw) ? pendingRaw : (pendingRaw?.items ?? []);

  // Fetch pending leave approvals (backend)
  const { data: pendingLeavesResponse, isLoading: leavesLoading } = useQuery({
    queryKey: [QUERY_KEYS.pendingApprovals, 'leaves'],
    queryFn: () => leaveService.getPendingApprovals(),
    enabled: !!user && !!isReviewer,
    staleTime: 60000,
  });
  const pendingLeaves: LeaveRequest[] = Array.isArray(pendingLeavesResponse?.data)
    ? pendingLeavesResponse.data
    : [];

  const totalPendingApprovals = (pendingReviews.length || 0) + (pendingLeaves.length || 0);

  // Fetch my daily performance
  const { data: performanceData, isLoading: perfLoading } = useQuery({
    queryKey: QUERY_KEYS.dailyPerformance(),
    queryFn: () => performanceService.getDailyPerformance(),
  });

  const performance = performanceData?.data;

  const getStatusColor = (status: ReviewStatus) => {
    switch (status) {
      case ReviewStatus.PENDING:
        return 'warning';
      case ReviewStatus.APPROVED:
        return 'success';
      case ReviewStatus.REWORK_REQUIRED:
        return 'info';
      case ReviewStatus.REJECTED:
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: ReviewStatus) => {
    switch (status) {
      case ReviewStatus.PENDING:
        return <Clock size={14} />;
      case ReviewStatus.APPROVED:
        return <CheckCircle size={14} />;
      case ReviewStatus.REWORK_REQUIRED:
        return <AlertCircle size={14} />;
      case ReviewStatus.REJECTED:
        return <AlertCircle size={14} />;
      default:
        return null;
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, pb: isMobile ? 10 : 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight={600}>
          Reviews & Approval
        </Typography>
        {!isMobile && (
          <Typography variant="body2" color="text.secondary">
            All pending task reviews and approvals requiring your action
          </Typography>
        )}
      </Box>

      {/* My Performance Summary */}
      {perfLoading ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: { xs: 1.5, sm: 2 }, mb: 3 }}>
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} variant="rounded" height={isMobile ? 80 : 100} />
          ))}
        </Box>
      ) : performance && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: { xs: 1.5, sm: 2 }, mb: 3 }}>
          {/* Hit Rate */}
          <Card>
            <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Star size={isMobile ? 14 : 18} style={{ color: '#f59e0b' }} />
                <Typography variant={isMobile ? 'caption' : 'subtitle2'} color="text.secondary">
                  {isMobile ? 'Hit Rate' : "Today's Hit Rate"}
                </Typography>
              </Box>
              <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight={700}>
                {performance.hitRate}%
              </Typography>
            </CardContent>
          </Card>

          {/* Completed Tasks */}
          <Card>
            <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <CheckCircle size={isMobile ? 14 : 18} style={{ color: '#16a34a' }} />
                <Typography variant={isMobile ? 'caption' : 'subtitle2'} color="text.secondary">
                  Completed
                </Typography>
              </Box>
              <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight={700}>
                {performance.tasksCompleted?.length || 0}
              </Typography>
            </CardContent>
          </Card>

          {/* Hits */}
          <Card>
            <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <CheckCircle size={isMobile ? 14 : 18} style={{ color: '#2563eb' }} />
                <Typography variant={isMobile ? 'caption' : 'subtitle2'} color="text.secondary">
                  Hits
                </Typography>
              </Box>
              <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight={700}>
                {performance.hitCount}
              </Typography>
            </CardContent>
          </Card>

          {/* Misses */}
          <Card>
            <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <AlertCircle size={isMobile ? 14 : 18} style={{ color: '#ef4444' }} />
                <Typography variant={isMobile ? 'caption' : 'subtitle2'} color="text.secondary">
                  Misses
                </Typography>
              </Box>
              <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight={700}>
                {performance.missCount}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Tabs */}
      <Card>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          variant={isMobile ? 'fullWidth' : 'standard'}
          sx={{ borderBottom: '1px solid', borderColor: 'divider', px: { xs: 1, sm: 2 } }}
        >
          <Tab label={isMobile ? 'Reviews' : 'All Reviews'} sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }} />
          {isReviewer && (
            <Tab 
              label={isMobile ? `Pending (${totalPendingApprovals})` : `Pending Approvals (${totalPendingApprovals})`} 
              sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
            />
          )}
        </Tabs>

        {/* All Reviews Tab */}
        <TabPanel value={activeTab} index={0}>
          <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
            {reviewsLoading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} variant="rounded" height={80} />
                ))}
              </Box>
            ) : reviews.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: { xs: 4, sm: 6 } }}>
                <Star size={isMobile ? 40 : 48} style={{ color: '#94a3b8', marginBottom: 16 }} />
                <Typography variant={isMobile ? 'subtitle1' : 'h6'} color="text.secondary" gutterBottom>
                  No reviews yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Submit tasks for review to see them here
                </Typography>
              </Box>
            ) : isMobile ? (
              /* Mobile Card View */
              <Stack spacing={1.5}>
                {reviews.map((review: Review) => (
                  <Card key={review.id} variant="outlined" sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                      <Chip
                        label={review.type}
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                      <Chip
                        icon={getStatusIcon(review.status) || undefined}
                        label={review.status.replace('_', ' ')}
                        size="small"
                        color={getStatusColor(review.status)}
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </Box>
                    <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                      {review.targetId}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary">
                        {dayjs(review.createdAt).format('MMM D, YYYY')}
                      </Typography>
                      {review.rating && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Star size={12} style={{ color: '#f59e0b' }} />
                          <Typography variant="caption">{review.rating}/5</Typography>
                        </Box>
                      )}
                    </Box>
                  </Card>
                ))}
              </Stack>
            ) : (
              /* Desktop Table View */
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell>Target</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Rating</TableCell>
                      <TableCell>Created</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reviews.map((review: Review) => (
                      <TableRow key={review.id} hover>
                        <TableCell>
                          <Chip
                            label={review.type}
                            size="small"
                            sx={{ textTransform: 'capitalize' }}
                          />
                        </TableCell>
                        <TableCell>{review.targetId}</TableCell>
                        <TableCell>
                          <Chip
                            icon={getStatusIcon(review.status) || undefined}
                            label={review.status.replace('_', ' ')}
                            size="small"
                            color={getStatusColor(review.status)}
                            sx={{ textTransform: 'capitalize' }}
                          />
                        </TableCell>
                        <TableCell>
                          {review.rating ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Star size={14} style={{ color: '#f59e0b' }} />
                              <Typography variant="body2">{review.rating}/5</Typography>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">-</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {dayjs(review.createdAt).format('MMM D, YYYY')}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </TabPanel>

        {/* Pending Approvals Tab (for managers) */}
        {isReviewer && (
          <TabPanel value={activeTab} index={1}>
            <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
              {(pendingLoading || leavesLoading) ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} variant="rounded" height={80} />
                  ))}
                </Box>
              ) : totalPendingApprovals === 0 ? (
                <Box sx={{ textAlign: 'center', py: { xs: 4, sm: 6 } }}>
                  <CheckCircle size={isMobile ? 40 : 48} style={{ color: '#16a34a', marginBottom: 16 }} />
                  <Typography variant={isMobile ? 'subtitle1' : 'h6'} color="text.secondary" gutterBottom>
                    All caught up!
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    No pending approvals at the moment
                  </Typography>
                </Box>
              ) : (
                <Box>
                  {/* Leave Approvals Section */}
                  {pendingLeaves.length > 0 && (
                    <Box sx={{ mb: 4 }}>
                      <Typography variant={isMobile ? 'subtitle1' : 'h6'} fontWeight={600} sx={{ mb: 2 }}>
                        Leave Requests ({pendingLeaves.length})
                      </Typography>
                      {isMobile ? (
                        /* Mobile Card View for Leaves */
                        <Stack spacing={1.5}>
                          {pendingLeaves.map((leave: LeaveRequest) => (
                            <Card key={leave.id} variant="outlined" sx={{ p: 2 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                  <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: '0.75rem' }}>
                                    {(leave.userName || leave.userId)?.charAt(0) || 'U'}
                                  </Avatar>
                                  <Box>
                                    <Typography variant="body2" fontWeight={600}>{leave.userName || leave.userId}</Typography>
                                    <Chip label={leave.type} size="small" color="info" sx={{ textTransform: 'capitalize', height: 20 }} />
                                  </Box>
                                </Box>
                                <Chip label={leave.status} size="small" color="warning" sx={{ textTransform: 'capitalize' }} />
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, color: 'text.secondary' }}>
                                <Calendar size={14} />
                                <Typography variant="caption">
                                  {dayjs(leave.startDate).format('MMM D')} - {dayjs(leave.endDate).format('MMM D')} ({leave.totalDays} days)
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                  fullWidth
                                  size="small"
                                  variant="contained"
                                  color="success"
                                  onClick={() => dispatch(openModal({ 
                                    type: 'leaveApproval', 
                                    data: { leaveId: leave.id, action: 'approve' } 
                                  }))}
                                  sx={{ minHeight: 36 }}
                                >
                                  Approve
                                </Button>
                                <Button
                                  fullWidth
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  onClick={() => dispatch(openModal({ 
                                    type: 'leaveApproval', 
                                    data: { leaveId: leave.id, action: 'reject' } 
                                  }))}
                                  sx={{ minHeight: 36 }}
                                >
                                  Reject
                                </Button>
                              </Box>
                            </Card>
                          ))}
                        </Stack>
                      ) : (
                        /* Desktop Table View for Leaves */
                        <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Employee</TableCell>
                              <TableCell>Type</TableCell>
                              <TableCell>Period</TableCell>
                              <TableCell>Days</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell>Action</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {pendingLeaves.map((leave: LeaveRequest) => (
                              <TableRow key={leave.id} hover>
                                <TableCell>
                                  <Typography variant="body2" fontWeight={500}>
                                    {leave.userName || leave.userId}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={leave.type}
                                    size="small"
                                    sx={{ textTransform: 'capitalize' }}
                                    color="info"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" color="text.secondary">
                                    {dayjs(leave.startDate).format('MMM D')} - {dayjs(leave.endDate).format('MMM D, YYYY')}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">{leave.totalDays} days</Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={leave.status}
                                    size="small"
                                    color="warning"
                                    sx={{ textTransform: 'capitalize' }}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button
                                      size="small"
                                      variant="contained"
                                      color="success"
                                      onClick={() => dispatch(openModal({ 
                                        type: 'leaveApproval', 
                                        data: { leaveId: leave.id, action: 'approve' } 
                                      }))}
                                    >
                                      Approve
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      color="error"
                                      onClick={() => dispatch(openModal({ 
                                        type: 'leaveApproval', 
                                        data: { leaveId: leave.id, action: 'reject' } 
                                      }))}
                                    >
                                      Reject
                                    </Button>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      )}
                    </Box>
                  )}

                  {/* Task Reviews Section */}
                  {pendingReviews.length > 0 && (
                    <Box>
                      <Typography variant={isMobile ? 'subtitle1' : 'h6'} fontWeight={600} sx={{ mb: 2 }}>
                        Task Reviews ({pendingReviews.length})
                      </Typography>
                      {isMobile ? (
                        /* Mobile Card View for Task Reviews */
                        <Stack spacing={1.5}>
                          {pendingReviews.map((review: Review) => (
                            <Card key={review.id} variant="outlined" sx={{ p: 2 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                                <Chip
                                  label={review.type}
                                  size="small"
                                  sx={{ textTransform: 'capitalize' }}
                                />
                                <Typography variant="caption" color="text.secondary">
                                  {dayjs(review.createdAt).format('MMM D, YYYY')}
                                </Typography>
                              </Box>
                              <Typography variant="body2" fontWeight={500} sx={{ mb: 2 }}>
                                {review.targetId}
                              </Typography>
                              <Button 
                                fullWidth
                                size="small" 
                                variant="contained"
                                onClick={() => handleReview(review)}
                                sx={{ minHeight: 36 }}
                              >
                                Review
                              </Button>
                            </Card>
                          ))}
                        </Stack>
                      ) : (
                        /* Desktop Table View for Task Reviews */
                        <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Type</TableCell>
                              <TableCell>Target</TableCell>
                              <TableCell>Submitted</TableCell>
                              <TableCell>Action</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {pendingReviews.map((review: Review) => (
                              <TableRow key={review.id} hover>
                                <TableCell>
                                  <Chip
                                    label={review.type}
                                    size="small"
                                    sx={{ textTransform: 'capitalize' }}
                                  />
                                </TableCell>
                                <TableCell>{review.targetId}</TableCell>
                                <TableCell>
                                  <Typography variant="body2" color="text.secondary">
                                    {dayjs(review.createdAt).format('MMM D, YYYY')}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Button 
                                    size="small" 
                                    variant="contained"
                                    onClick={() => handleReview(review)}
                                  >
                                    Review
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      )}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </TabPanel>
        )}
      </Card>
    </Box>
  );
};

export default ReviewsPage;
